/**
 * The selected restaurant's pin: how much it is enlarged and how it bounces
 * when it becomes selected.
 *
 * The pin is drawn by its own one-feature symbol layer (`selected-restaurant`,
 * see layers.tsx), above all other pins. The bounce moves that layer's
 * `icon-translate` paint property each frame (a paint property, so Mapbox does
 * not re-run symbol layout, and no image is re-registered or source re-parsed).
 * The icon is anchored at the pin's tip, so the enlargement grows upward from
 * the tip and the bounce lifts the pin off it and settles back.
 */

/** Selected pin size relative to a normal pin (0.075 icon-size). Chosen within CD's 1.4-1.5x range. */
export const SELECTED_PIN_SCALE = 1.45;
/** Length of the strong intro bounce, played when a restaurant becomes selected. */
export const BOUNCE_DURATION_MS = 2000;
/** Height of the intro envelope in px (the first hop peaks at about 60% of this). */
export const BOUNCE_AMPLITUDE_PX = 40;
/** How many hops fit in the intro, and how quickly they die away. */
export const BOUNCE_COUNT = 3;
export const BOUNCE_DECAY = 3;
/**
 * After the intro the pin keeps a small, slow float for as long as it is the
 * selected one, so it stays identifiable: `STEADY_AMPLITUDE_PX` at most, one
 * rise-and-fall every `STEADY_PERIOD_MS`, eased (sin squared: zero speed at the
 * tip and at the top, so it breathes rather than ticks).
 */
export const STEADY_AMPLITUDE_PX = 3;
export const STEADY_PERIOD_MS = 2400;
/**
 * In the steady state the lift changes by a fraction of a pixel per frame, and
 * every apply repaints the map, so it is only re-applied when it moved by at
 * least this much. 0.25px (a bit under one device pixel on a 3x screen, about a
 * dozen steps per rise) keeps the float smooth; 0.5px risked visible ticking on
 * a 3px float. The paint property has no transition (see layers.tsx), so each
 * step lands at once.
 */
export const STEADY_MIN_STEP_PX = 0.25;

/**
 * Vertical lift (px, 0 = resting on the tip) at `progress` (0 to 1) through
 * the intro: decaying hops, |sin| so the pin returns to rest between them,
 * exactly 0 at the start and end.
 */
export function getBounceOffset(progress: number, amplitude = BOUNCE_AMPLITUDE_PX): number {
  if (!(progress > 0) || progress >= 1) return 0;
  return amplitude * Math.exp(-BOUNCE_DECAY * progress) * Math.abs(Math.sin(Math.PI * BOUNCE_COUNT * progress));
}

/** Steady-state lift `sinceIntroMs` after the intro ended: 0 at the handoff, then a slow eased float. */
export function getSteadyOffset(sinceIntroMs: number): number {
  if (!(sinceIntroMs > 0)) return 0;
  const phase = Math.sin((Math.PI * sinceIntroMs) / STEADY_PERIOD_MS);
  return STEADY_AMPLITUDE_PX * phase * phase;
}

/**
 * The pin's lift at `elapsedMs` since it became selected: the strong intro,
 * then the steady float. Both are exactly 0 at the handoff, so there is no jump.
 * Under reduced motion the pin is only enlarged: always 0.
 */
export function getPinLift(elapsedMs: number, reducedMotion = false): number {
  if (reducedMotion || !(elapsedMs > 0)) return 0;
  if (elapsedMs < BOUNCE_DURATION_MS) return getBounceOffset(elapsedMs / BOUNCE_DURATION_MS);
  return getSteadyOffset(elapsedMs - BOUNCE_DURATION_MS);
}

/** Page visibility, injectable for tests. */
export interface VisibilitySource {
  isHidden: () => boolean;
  subscribe: (onChange: () => void) => () => void;
}

const documentVisibility: VisibilitySource = {
  isHidden: () => typeof document !== 'undefined' && document.hidden,
  subscribe: (onChange) => {
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  },
};

interface BounceOptions {
  /** Receives the current lift in px (0 when stopped). */
  apply: (liftPx: number) => void;
  /** Under reduced motion the pin is only enlarged: no frames are scheduled. */
  reducedMotion: boolean;
  // Injectable for tests.
  now?: () => number;
  requestFrame?: (callback: () => void) => number;
  cancelFrame?: (handle: number) => void;
  visibility?: VisibilitySource;
}

/**
 * Runs the pin's motion for one selection: one requestAnimationFrame loop, the
 * strong intro then the steady float, until the returned cancel function is
 * called (selection changed, card closed, unmount). The loop pauses while the
 * page is hidden and resumes in the same phase (the hidden time is not counted,
 * so the intro is never skipped). Cancelling stops the loop, removes the
 * visibility listener and returns the pin to rest; it is safe to call twice.
 */
export function startPinBounce({
  apply,
  reducedMotion,
  now = () => performance.now(),
  requestFrame = (callback) => requestAnimationFrame(callback),
  cancelFrame = (handle) => cancelAnimationFrame(handle),
  visibility = documentVisibility,
}: BounceOptions): () => void {
  if (reducedMotion) {
    apply(0);
    return () => {};
  }
  let startedAt = now();
  let pausedAt: number | null = null;
  let handle: number | null = null;
  let stopped = false;
  let lastApplied = 0;

  const tick = () => {
    handle = null;
    const elapsed = now() - startedAt;
    const lift = getPinLift(elapsed);
    // In the steady state skip updates smaller than a step (each one repaints the map).
    if (elapsed < BOUNCE_DURATION_MS || Math.abs(lift - lastApplied) >= STEADY_MIN_STEP_PX) {
      lastApplied = lift;
      apply(lift);
    }
    handle = requestFrame(tick);
  };

  const onVisibilityChange = () => {
    if (stopped) return;
    if (visibility.isHidden()) {
      if (handle !== null) cancelFrame(handle);
      handle = null;
      pausedAt = now();
    } else if (pausedAt !== null) {
      startedAt += now() - pausedAt;
      pausedAt = null;
      if (handle === null) handle = requestFrame(tick);
    }
  };
  const unsubscribe = visibility.subscribe(onVisibilityChange);

  if (visibility.isHidden()) pausedAt = now();
  else handle = requestFrame(tick);

  return () => {
    if (stopped) return;
    stopped = true;
    unsubscribe();
    if (handle !== null) cancelFrame(handle);
    handle = null;
    apply(0);
  };
}
